<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  // Contas antigas (lista vazia = "pode tudo") entram com tudo marcado,
  // preservando o comportamento ate o usuario mudar.
  legacyAll: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['legacyResolved']);
// Cada lista guarda os campos que a IA pode preencher, no formato
// [{ attributeKey, instruction }]. Entidade sem nenhum campo marcado nao e
// preenchida (o backend trata isso com attribute_filling_mode = 'selected').
const contact = defineModel('contact', { type: Array, default: () => [] });
const company = defineModel('company', { type: Array, default: () => [] });
const deal = defineModel('deal', { type: Array, default: () => [] });

const { t } = useI18n();
const store = useStore();

const contactDefinitions = useMapGetter('attributes/getContactAttributes');
const companyDefinitions = useMapGetter('attributes/getCompanyAttributes');
const dealDefinitions = useMapGetter('attributes/getDealAttributes');

const SOCIAL_FIELD_KEYS = [
  'social_facebook',
  'social_github',
  'social_instagram',
  'social_telegram',
  'social_tiktok',
  'social_linkedin',
  'social_twitter',
];

const FIXED_CONTACT_FIELD_KEYS = [
  'name',
  'email',
  'phone_number',
  'description',
  'city',
  'country',
  'country_code',
  ...SOCIAL_FIELD_KEYS,
];

const FIXED_COMPANY_FIELD_KEYS = [
  'name',
  'domain',
  'email',
  'phone_number',
  'cnpj',
  'city',
  'country_code',
  'description',
  ...SOCIAL_FIELD_KEYS,
];

const FIXED_DEAL_FIELD_KEYS = [
  'name',
  'pipeline',
  'value',
  'currency',
  'products_of_interest',
  'last_purchase_at',
  'pipeline_stage',
];

const ENTITY_CONFIG = [
  {
    key: 'contact',
    section: 'CONTACT_ATTRIBUTES_ALLOWLIST',
    icon: 'i-lucide-contact',
    fixedKeys: FIXED_CONTACT_FIELD_KEYS,
    model: contact,
    definitions: contactDefinitions,
  },
  {
    key: 'company',
    section: 'COMPANY_ATTRIBUTES_ALLOWLIST',
    icon: 'i-lucide-building',
    fixedKeys: FIXED_COMPANY_FIELD_KEYS,
    model: company,
    definitions: companyDefinitions,
  },
  {
    key: 'deal',
    section: 'DEAL_ATTRIBUTES_ALLOWLIST',
    icon: 'i-lucide-dollar-sign',
    fixedKeys: FIXED_DEAL_FIELD_KEYS,
    model: deal,
    definitions: dealDefinitions,
  },
];

const openEntity = ref(null);

const standardFields = entity =>
  entity.fixedKeys.map(attributeKey => ({
    attributeKey,
    label: t(
      `CAPTAIN.ASSISTANTS.FORM.${entity.section}.FIXED_FIELDS.${attributeKey}`
    ),
  }));

const customFields = entity =>
  entity.definitions.value.map(definition => ({
    attributeKey: definition.attributeKey,
    label: definition.attributeDisplayName,
  }));

const allKeys = entity =>
  [...standardFields(entity), ...customFields(entity)].map(
    field => field.attributeKey
  );

const entries = computed(() =>
  ENTITY_CONFIG.map(entity => ({
    ...entity,
    standard: standardFields(entity),
    custom: customFields(entity),
  }))
);

const selectedCount = entity => entity.model.value.length;
const totalCount = entity => entity.standard.length + entity.custom.length;

const findEntry = (entity, attributeKey) =>
  entity.model.value.find(entry => entry.attributeKey === attributeKey);

const isSelected = (entity, attributeKey) => !!findEntry(entity, attributeKey);

const toggleField = (entity, attributeKey) => {
  entity.model.value = isSelected(entity, attributeKey)
    ? entity.model.value.filter(entry => entry.attributeKey !== attributeKey)
    : [...entity.model.value, { attributeKey, instruction: '' }];
};

const setInstruction = (entity, attributeKey, instruction) => {
  entity.model.value = entity.model.value.map(entry =>
    entry.attributeKey === attributeKey ? { ...entry, instruction } : entry
  );
};

const selectAll = entity => {
  const current = entity.model.value;
  entity.model.value = allKeys(entity).map(
    attributeKey =>
      current.find(entry => entry.attributeKey === attributeKey) || {
        attributeKey,
        instruction: '',
      }
  );
};

const clearAll = entity => {
  entity.model.value = [];
};

const toggleOpen = key => {
  openEntity.value = openEntity.value === key ? null : key;
};

// Marca todos os campos das entidades em modo legado (so depois de carregar as
// definicoes, para incluir tambem os campos personalizados).
const definitionsLoaded = ref(false);

const resolveLegacy = () => {
  if (!definitionsLoaded.value) return;
  const resolved = ENTITY_CONFIG.filter(
    entity => props.legacyAll[entity.key] && !entity.model.value.length
  );
  if (!resolved.length) return;
  resolved.forEach(selectAll);
  emit(
    'legacyResolved',
    resolved.map(entity => entity.key)
  );
};

watch([definitionsLoaded, () => props.legacyAll], resolveLegacy, {
  deep: true,
});

onMounted(async () => {
  await store.dispatch('attributes/get');
  definitionsLoaded.value = true;
});
</script>

<template>
  <section class="flex flex-col gap-4">
    <div class="flex flex-col gap-1">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.FIELD_FILLING.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11">
        {{ t('CAPTAIN.ASSISTANTS.FORM.FIELD_FILLING.DESCRIPTION') }}
      </p>
    </div>

    <div class="flex flex-col gap-3">
      <div
        v-for="entity in entries"
        :key="entity.key"
        class="border rounded-xl border-n-weak bg-n-solid-1 overflow-hidden"
      >
        <button
          type="button"
          class="flex items-center w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-n-alpha-1"
          :aria-expanded="openEntity === entity.key"
          @click="toggleOpen(entity.key)"
        >
          <span
            class="flex items-center justify-center rounded-lg size-8 shrink-0 bg-n-alpha-2"
          >
            <Icon :icon="entity.icon" class="size-4 text-n-slate-11" />
          </span>
          <span class="flex flex-col flex-1 min-w-0">
            <span class="text-sm font-medium text-n-slate-12">
              {{
                t(
                  `CAPTAIN.ASSISTANTS.FORM.FIELD_FILLING.ENTITIES.${entity.key.toUpperCase()}`
                )
              }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{
                selectedCount(entity)
                  ? t('CAPTAIN.ASSISTANTS.FORM.FIELD_FILLING.SELECTED_COUNT', {
                      selected: selectedCount(entity),
                      total: totalCount(entity),
                    })
                  : t('CAPTAIN.ASSISTANTS.FORM.FIELD_FILLING.NONE_SELECTED')
              }}
            </span>
          </span>
          <span
            class="i-lucide-chevron-down size-4 shrink-0 text-n-slate-10 transition-transform duration-200"
            :class="{ 'rotate-180': openEntity === entity.key }"
            aria-hidden="true"
          />
        </button>

        <div
          v-if="openEntity === entity.key"
          class="flex flex-col gap-4 px-4 pt-3 pb-4 border-t border-n-weak"
        >
          <p class="text-sm text-n-slate-11">
            {{
              t(
                `CAPTAIN.ASSISTANTS.FORM.FIELD_FILLING.ENTITY_DESCRIPTIONS.${entity.key.toUpperCase()}`
              )
            }}
          </p>
          <div class="flex items-center gap-1">
            <Button
              :label="t('CAPTAIN.ASSISTANTS.FORM.FIELD_FILLING.SELECT_ALL')"
              variant="ghost"
              color="blue"
              size="xs"
              @click="selectAll(entity)"
            />
            <Button
              :label="t('CAPTAIN.ASSISTANTS.FORM.FIELD_FILLING.CLEAR_ALL')"
              variant="ghost"
              color="slate"
              size="xs"
              :disabled="!selectedCount(entity)"
              @click="clearAll(entity)"
            />
          </div>

          <div
            v-for="group in [
              { id: 'standard', fields: entity.standard },
              { id: 'custom', fields: entity.custom },
            ]"
            v-show="group.fields.length"
            :key="group.id"
            class="flex flex-col gap-2"
          >
            <span
              class="text-xs font-medium tracking-wide uppercase text-n-slate-10"
            >
              {{
                group.id === 'standard'
                  ? t('CAPTAIN.ASSISTANTS.FORM.FIELD_FILLING.STANDARD_FIELDS')
                  : t('CAPTAIN.ASSISTANTS.FORM.FIELD_FILLING.CUSTOM_FIELDS')
              }}
            </span>

            <div
              v-for="field in group.fields"
              :key="field.attributeKey"
              class="flex flex-col gap-2 p-3 rounded-lg transition-colors"
              :class="
                isSelected(entity, field.attributeKey) ? 'bg-n-alpha-1' : ''
              "
            >
              <div class="flex items-center gap-3">
                <Switch
                  :model-value="isSelected(entity, field.attributeKey)"
                  @update:model-value="toggleField(entity, field.attributeKey)"
                />
                <span class="text-sm text-n-slate-12">{{ field.label }}</span>
              </div>
              <TextArea
                v-if="isSelected(entity, field.attributeKey)"
                :model-value="findEntry(entity, field.attributeKey).instruction"
                :label="
                  t('CAPTAIN.ASSISTANTS.FORM.FIELD_FILLING.INSTRUCTION_LABEL')
                "
                :placeholder="
                  t(
                    entity.key === 'deal' &&
                      field.attributeKey === 'pipeline_stage'
                      ? 'CAPTAIN.ASSISTANTS.FORM.FIELD_FILLING.STAGE_INSTRUCTION_PLACEHOLDER'
                      : 'CAPTAIN.ASSISTANTS.FORM.FIELD_FILLING.INSTRUCTION_PLACEHOLDER'
                  )
                "
                :max-length="300"
                auto-height
                @update:model-value="
                  setInstruction(entity, field.attributeKey, $event)
                "
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
